//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import DataSource from 'devextreme/data/data_source'
import 'devextreme/ui/button'
import 'devextreme/ui/switch'
import 'devextreme/ui/popup'

//#endregion

import { $Q_SERVICE_NAME, $SCOPE_SERVICE_NAME, $TIMEOUT_SERVICE_NAME } from '../../../services/system'
import { USER_SESSION_SERVICE_NAME } from '../../../services/user-session'
import { LocalDictionaryFactory } from '../../api/api.module'
import { ColumnModel } from '../../api/api.models'
import { EventsNames } from '../../../helpers/events-names'
import { GridButtonBridge } from '../../common/grid-button/grid-button.directive'
import { i18n } from './../i18n/common.i18n'
import { GridOptions, GridTypes } from '../grid/grid.controller'
import { Md5 } from '../../../helpers/md5-helper'
import { GridBridge } from '../common.interfaces'
import { OpDeviceHierarchy, OpDevice } from '../../../models/core'
import { IUserSession } from '../../../helpers/user-session'
import { TemplateHelper, ConfigHelper } from '../../../helpers'
import { Enums } from '../../../models/common/enums'

//#region Asset Imports

import './grid-slot-edit.scss'
import * as TEMPLATE from './grid-slot-edit.tpl.html'

//#endregion

interface GridSlotEditScope extends ng.IScope {
	gridOptions: GridOptions
	grid: DevExpress.ui.dxDataGrid
	rowSelected: boolean
	bridge: GridButtonBridge
	saveButtonDisabled: boolean

	toggleStateButtonIcon(): string
	toggleState(): void
	switchState: boolean
	stateSwitch: DevExpress.ui.dxSwitch
	stateSwitchOptions: DevExpress.ui.dxSwitchOptions
	removeButtonOptions: DevExpress.ui.dxButtonOptions
	saveButtonOptions: DevExpress.ui.dxButtonOptions

	source: GridBridge
	popupOptions: DevExpress.ui.dxPopupOptions
	popupVisible: boolean

	changed: OpDeviceHierarchy[]
	inputHierarchy: OpDeviceHierarchy[]

	isLoading: boolean
	selectedRowHasDevice: boolean
}

export class GridSlotEditDirective implements ng.IDirective {
	previousSaveRequestMd5: string
	previousElementsMd5: string
	saveFailed: boolean = false

	constructor() { }

	restrict = 'E'
	scope = {
		source: '=',
	}
	template = TEMPLATE

	link = (scope, element, attrs) => {
		scope.$watch('source', (value) => {
			scope.source = value
		})
	}

	controller = [
		$SCOPE_SERVICE_NAME,
		$Q_SERVICE_NAME,
		$TIMEOUT_SERVICE_NAME,
		'localDictionary',
		USER_SESSION_SERVICE_NAME,
		($scope: GridSlotEditScope, $q: ng.IQService, $timeout: ng.ITimeoutService, localDictionary: LocalDictionaryFactory, userSession: IUserSession) => {
			$scope.popupVisible = false
			$scope.popupOptions = {
				showTitle: true,
				showCloseButton: true,
				title: $scope.source.title,
				bindingOptions: {
					visible: 'popupVisible',
				},
				onHiding: () => {
					$scope.$root.$broadcast(EventsNames.CLOSE_GRID_EDIT_POPUP + $scope.source.popupId)
				},
				onShowing: () => {
					this.previousSaveRequestMd5 = ''
					this.previousElementsMd5 = ''
					$scope.isLoading = false
				},
			}

			$scope.$on(EventsNames.OPEN_GRID_EDIT_POPUP + $scope.source.popupId, (event: ng.IAngularEvent, ...args: any[]) => {
				onShowUp()
			})

			//update grid dataSource while popup is opened
			$scope.$on(EventsNames.OPEN_GRID_EDIT_POPUP + $scope.source.popupId + 'updateDataSource', (event: ng.IAngularEvent, ...args: any[]) => {
				$scope.source.items = args[0]
				$scope.inputHierarchy = []

				if ($scope.source.items != null) {
					$scope.source.items.forEach((hierarchy: OpDeviceHierarchy) => {
						$scope.inputHierarchy.push(new OpDeviceHierarchy(hierarchy))
					})
				}

				if ($scope.grid != null) {
					$scope.grid.option('dataSource', new DataSource(args[0]))
				}

				setTimeout(() => {
					$scope.isLoading = false
				})
			})

			$scope.switchState = null

			$scope.stateSwitchOptions = {
				height: '100%',
				onInitialized: (e: any) => {
					$scope.stateSwitch = e.component
				},
				onValueChanged: (e: any) => {
					if ($scope.grid && e.event) {
						const rowData: OpDeviceHierarchy[] = $scope.grid.getSelectedRowsData()
						const currentMd5: string = Md5.init(rowData)

						if (this.previousElementsMd5 !== currentMd5) {
							this.previousElementsMd5 = currentMd5

							if (_.size(rowData) > 0) {
								const hierarhy: OpDeviceHierarchy = rowData[0]
								hierarhy.isActive = e.value
								$scope.saveButtonDisabled = false

								let isAlreadyInChanged: boolean = false

								if (hierarhy.idDeviceHierarchy == 0) {
									const selectedRowIndex: number = $scope.grid.getRowIndexByKey($scope.grid.getSelectedRowKeys()[0])
									hierarhy.idDeviceHierarchy = selectedRowIndex != -1 ? -selectedRowIndex : 0
								}

								if (!$scope.changed) {
									$scope.changed = []
								}

								$scope.changed.forEach((changed: OpDeviceHierarchy) => {
									if (changed.idDeviceHierarchy == hierarhy.idDeviceHierarchy) {
										changed.isActive = e.value
										isAlreadyInChanged = true
									}
								})

								if (!isAlreadyInChanged) {
									$scope.changed.push(hierarhy)
								}

								hierarhy.opState = Enums.OpChangeState.Modified

								$timeout(() => {
									$scope.grid.clearSelection()
								})
							}
						}
					}
				},
				bindingOptions: {
					disabled: '!rowSelected || (!selectedRowHasDevice && !switchState)',
					value: 'switchState',
				},
			}

			const clearSlot = () => {
				if ($scope.grid) {
					const entity: OpDeviceHierarchy = <OpDeviceHierarchy>$scope.grid.getSelectedRowsData()[0]
					entity.device = null
					entity.serialNbr = null

					$scope.saveButtonDisabled = false

					let isAlreadyInChanged: boolean = false

					if (entity.idDeviceHierarchy == 0) {
						const selectedRowIndex: number = $scope.grid.getRowIndexByKey($scope.grid.getSelectedRowKeys()[0])
						entity.idDeviceHierarchy = selectedRowIndex != -1 ? -selectedRowIndex : 0
					}

					if (!$scope.changed) {
						$scope.changed = []
					}

					$scope.changed.forEach((changed: OpDeviceHierarchy) => {
						if (changed.idDeviceHierarchy == entity.idDeviceHierarchy) {
							changed.device = null
							changed.serialNbr = null
							isAlreadyInChanged = true
						}
					})

					if (!isAlreadyInChanged) {
						$scope.changed.push(entity)
					}

					entity.opState = Enums.OpChangeState.Modified

					$timeout(() => {
						$scope.grid.clearSelection()
					})
				}
			}

			$scope.rowSelected = false

			const isSlotSelected = (): boolean => {
				return $scope.grid ? $scope.grid.getSelectedRowKeys().length != 0 : false
			}

			$scope.selectedRowHasDevice = false

			const isSelectedRowHasDevice = (): boolean => {
				if ($scope.grid && $scope.grid.getSelectedRowsData().length != 0) {
					return (<OpDeviceHierarchy>$scope.grid.getSelectedRowsData()[0]).device != null
				} else {
					return false
				}
			}

			$scope.saveButtonDisabled = true

			const saveChanges = () => {
				if (_.isFunction($scope.source.onSubmit)) {
					$scope.isLoading = true
					const currentMd5: string = Md5.init($scope.changed)

					if (this.previousSaveRequestMd5 !== currentMd5 || this.saveFailed) {
						this.previousSaveRequestMd5 = currentMd5

						$scope.grid
							.getDataSource()
							.store()
							.load()
							.done((currentHierarchy: OpDeviceHierarchy[]) => {

								currentHierarchy.forEach((hierarchy: OpDeviceHierarchy) => {
									if (hierarchy.idDeviceHierarchy < 0) {
										hierarchy.idDeviceHierarchy = 0
									}

									let source: OpDeviceHierarchy = null

									for (let item of $scope.inputHierarchy) {
										if (item.idDeviceHierarchy == hierarchy.idDeviceHierarchy) {
											source = item
											break
										}
									}

									if (source != null) {
										if (source.serialNbr == null && hierarchy.serialNbr != null && source.idDeviceHierarchy == 0) {
											hierarchy.opState = Enums.OpChangeState.New
										} else if (source.serialNbr != null && hierarchy.serialNbr == null) {
											hierarchy.opState = Enums.OpChangeState.Delete
										} else if (source.serialNbr != hierarchy.serialNbr || source.isActive != hierarchy.isActive) {
											hierarchy.opState = Enums.OpChangeState.Modified
										} else {
											hierarchy.opState = Enums.OpChangeState.Loaded
										}
									} else {
										hierarchy.opState = Enums.OpChangeState.Loaded
									}
								})

								$scope.source
									.onSubmit(null, null, currentHierarchy)
									.then((result: boolean) => {
										if (result == true) {
											this.saveFailed = false
											$scope.saveButtonDisabled = true
											onShowUp()
										} else {
											this.saveFailed = true
										}
									})
									.catch(() => {
										this.saveFailed = true
									})
							})
					}
				}
			}

			$scope.$watch(isSlotSelected, (value: boolean) => {
				$scope.rowSelected = $scope.bridge.enabled = value
			})

			$scope.$watch(isSelectedRowHasDevice, (value: boolean) => {
				$scope.selectedRowHasDevice = value
			})

			const load = () => {
				$scope.changed = []

				if ($scope.grid != null) {
					if ($scope.inputHierarchy) {
						$scope.source.items = []

						$scope.inputHierarchy.forEach((hierarchy: OpDeviceHierarchy) => {
							$scope.source.items.push(new OpDeviceHierarchy(hierarchy))
						})
					}

					$scope.grid.option('dataSource', new DataSource($scope.source.items))
				} else {
					if ($scope.source.items) {
						$scope.inputHierarchy = []

						$scope.source.items.forEach((hierarchy: OpDeviceHierarchy) => {
							$scope.inputHierarchy.push(new OpDeviceHierarchy(hierarchy))
						})
					}

					$scope.gridOptions = {
						name: 'gridslotedit',
						type: GridTypes.NO_SEARCH_BAR_SMALL,
						buttonsOptions: {
							enabled: false,
						},
						columnsDictionary: $scope.source.columns,
						dataSource: new DataSource($scope.source.items),
						onInitialized: (e) => {
							$scope.grid = e.component
						},
						selection: {
							mode: 'single',
						},
						onRowPrepared: (e: any) => {
							if (e.rowType == 'data' && e.data instanceof OpDeviceHierarchy) {
								const hierarchy: OpDeviceHierarchy = <OpDeviceHierarchy>e.data

								if (hierarchy.opState == Enums.OpChangeState.Modified) {
									$(e.rowElement).css('font-style', 'italic')
								}
							}
						},
						onSelectionChanged: (e) => {
							const rowsData: OpDeviceHierarchy[] = $scope.grid.getSelectedRowsData()

							if (rowsData && rowsData[0]) {
								$timeout(() => {
									$scope.switchState = rowsData[0].isActive || false
									$scope.stateSwitch.option('value', $scope.switchState)
									$scope.stateSwitch.repaint()
								})
							}
						},
						allowColumnReordering: $scope.source.allowColumnReordering != null ? $scope.source.allowColumnReordering : true,
					}
				}
			}

			$scope.bridge = {
				idReferenceType: Enums.ReferenceType.SerialNbr,
				getDataSourceMethod: userSession.getDataProvider().Device.getDeviceFilter,
				onSelect: (e) => {
					$timeout(() => {
						const device: OpDevice = new OpDevice(e.data['__BUSINESS_OBJECT__'])

						const deviceHierarchy: OpDeviceHierarchy = <OpDeviceHierarchy>$scope.grid.getSelectedRowsData()[0]

						deviceHierarchy.opState = Enums.OpChangeState.Modified

						deviceHierarchy.device = device
						deviceHierarchy.serialNbr = device.serialNbr

						$scope.saveButtonDisabled = false

						let isAlreadyInChanged: boolean = false

						if (deviceHierarchy.idDeviceHierarchy == 0) {
							const selectedRowIndex: number = $scope.grid.getRowIndexByKey($scope.grid.getSelectedRowKeys()[0])
							deviceHierarchy.idDeviceHierarchy = selectedRowIndex != -1 ? -selectedRowIndex : 0
						}

						if (!$scope.changed) {
							$scope.changed = []
						}

						$scope.changed.forEach((changed: OpDeviceHierarchy) => {
							if (changed.idDeviceHierarchy == deviceHierarchy.idDeviceHierarchy) {
								changed.device = deviceHierarchy.device
								changed.serialNbr = deviceHierarchy.serialNbr
								isAlreadyInChanged = true
							}
						})

						if (!isAlreadyInChanged) {
							$scope.changed.push(deviceHierarchy)
						}

						$timeout(() => {
							$scope.grid.clearSelection()
						})
					})
				},
				dataRequest: null,
				columnRequest: (): ng.IPromise<any> => {
					const defer: ng.IDeferred<any> = $q.defer<any>()

					userSession
						.getDataProvider()
						.System.getRequestColumnInfo(Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__POPUP_GRID_PARAMS, Enums.SimaxModuleName.Device, {
							groupReferenceType: Enums.ReferenceType.IdDistributor,
							groupReferenceValue: userSession.getLoggedOperator().idDistributor,
							viewReferenceType: Enums.ReferenceType.SerialNbr,
						})
						.then((columns: ColumnModel[]) => {
							defer.resolve(columns)
						})
						.fail((error: any) => defer.reject(error))

					return defer.promise
				},
				enabled: $scope.rowSelected,
				name: null,
			}

			$scope.removeButtonOptions = {
				onClick: clearSlot,
				template: TemplateHelper.getIcon(ConfigHelper.Icon.REMOVE, userSession),
				bindingOptions: {
					disabled: '!rowSelected || !selectedRowHasDevice',
				},
			}

			$scope.saveButtonOptions = {
				text: i18n('Common', 'Apply'),
				onClick: saveChanges,
				bindingOptions: {
					disabled: 'saveButtonDisabled',
				},
			}

			$scope.$watchCollection('source.items', (items: OpDeviceHierarchy[]) => {
				if ($scope.grid) {
					$scope.grid.option('dataSource').store().clear()

					items.forEach((item: OpDeviceHierarchy) => {
						$scope.grid.option('dataSource').store().insert(item)
					})

					$scope.grid.refresh()
				}
			})

			const onShowUp = () => {
				if ($scope.grid) {
					$scope.saveButtonDisabled = true
					$scope.grid.repaint()
				}

				load()
				$scope.popupVisible = true
			}
		},
	]
}
